import React, { useMemo, useRef, useState } from 'react';
import { ArrowLeft, FileUp, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../ui/sheet';
import {
  FUNDING_RIGHT_DRAWER_CLASS,
} from './fundingDrawerUi';
import FundingDocumentTypeSelector from './FundingDocumentTypeSelector';
import FundingUploadedDocumentCard from './FundingUploadedDocumentCard';
import {
  createUploadedDocument,
  validateUploadedDocuments,
} from './fundingDocumentTypes';
import {
  clientDataFromReviewFields,
  groupReviewFieldsByCategory,
} from '../../../utils/fundingClientIntakeUtils';
import {
  EXTRACTION_STATUS,
  EXTRACTION_UNAVAILABLE_DETAIL,
  EXTRACTION_UNAVAILABLE_TITLE,
  EXTRACTION_PARTIAL_TITLE,
  EXTRACTION_PARTIAL_MESSAGE,
  EXTRACTION_PARTIAL_MANUAL_HINT,
  EXTRACTION_COMPLETE_TITLE,
  EXTRACTION_IGNORED_OCR_WARNING,
  extractFromUploadedDocuments,
  SAVE_DOCS_ONLY_REQUIRES_CLIENT_MESSAGE,
} from '../../../services/funding/documentExtractionService';
import {
  cleanupFundingPreviewUrls,
  FUNDING_UPLOAD_ACCEPT,
  FUNDING_UPLOAD_DROPZONE_LABEL,
  normalizeFundingUploadFiles,
} from '../../../services/funding/fundingDocumentFileUtils';
import { saveUploadedDocumentsToClientSafe } from '../../../services/funding/fundingDocumentsService';

const STEPS = [
  { id: 'upload', label: '1. Upload files' },
  { id: 'scan', label: '2. Scan / extract' },
  { id: 'review', label: '3. Review data' },
  { id: 'create', label: '4. Create client' },
];

export const SAVE_MODES = [
  {
    id: 'create_and_save',
    title: 'Create client and save documents',
    description: 'Creates a new client from extracted fields and saves uploaded documents to the client vault.',
  },
  {
    id: 'create_info_only',
    title: 'Create client using extracted info only',
    description: 'Creates a new client from extracted fields. Uploaded files will not be saved to the client profile.',
  },
  {
    id: 'save_docs_only',
    title: 'Save documents only',
    description: 'Saves uploaded documents to an existing client vault without creating or updating client profile fields.',
  },
  {
    id: 'discard',
    title: 'Discard extraction',
    description: 'Clears extracted data and uploaded files, returning to the upload step.',
  },
];

const DEFAULT_UPLOAD_TYPES = ['Driver license / ID'];

export function hasMinimumExtractedClientData(reviewFields = [], clientData = null) {
  const merged = clientDataFromReviewFields(reviewFields, clientData || {});
  return Boolean(
    merged.firstName?.trim()
    || merged.lastName?.trim()
    || merged.ownerName?.trim()
    || merged.businessLegalName?.trim(),
  );
}

export function isExtractionUnavailable(extractionStatus, documents = []) {
  if (extractionStatus === EXTRACTION_STATUS.UNAVAILABLE) return true;
  if (!documents.length) return false;
  return documents.every((doc) => (
    !doc.useForExtraction
    || doc.scanStatus === EXTRACTION_STATUS.UNAVAILABLE
  ));
}

export function isSaveModeAvailable(modeId, {
  existingClientId,
  hasExtractedFields,
  hasMinimumFields,
  extractionUnavailable = false,
  extractionPartial = false,
  saveDocumentsToProfile = false,
}) {
  if (extractionUnavailable) {
    return modeId === 'save_docs_only' ? Boolean(existingClientId) : false;
  }
  if (modeId === 'save_docs_only') {
    return Boolean(existingClientId);
  }
  if (modeId === 'create_and_save') {
    if (saveDocumentsToProfile && hasExtractedFields) {
      return true;
    }
    if (extractionPartial && saveDocumentsToProfile) {
      return true;
    }
    return hasExtractedFields && hasMinimumFields;
  }
  if (modeId === 'create_info_only') {
    return hasExtractedFields;
  }
  return true;
}

export function getAvailableSaveModes(options) {
  return SAVE_MODES.filter((mode) => isSaveModeAvailable(mode.id, options));
}

export function resolveDefaultSaveMode(options) {
  const available = getAvailableSaveModes(options);
  const nonDiscard = available.filter((mode) => mode.id !== 'discard');
  if (nonDiscard.some((mode) => mode.id === 'create_and_save')) {
    return 'create_and_save';
  }
  if (nonDiscard.some((mode) => mode.id === 'create_info_only')) {
    return 'create_info_only';
  }
  if (nonDiscard.some((mode) => mode.id === 'save_docs_only')) {
    return 'save_docs_only';
  }
  return nonDiscard[0]?.id || 'create_and_save';
}

const UploadDocumentsDrawer = ({
  open,
  onOpenChange,
  onCreateClient,
  onBackToOptions,
  onContinueManualIntake,
  onAuthError,
  existingClientId = null,
  agencyId = null,
  initialDocuments = [],
  initialDefaultTypes = DEFAULT_UPLOAD_TYPES,
}) => {
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [defaultTypes, setDefaultTypes] = useState(initialDefaultTypes);
  const [workflowStep, setWorkflowStep] = useState('upload');
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [reviewFields, setReviewFields] = useState([]);
  const [clientData, setClientData] = useState(null);
  const [saveMode, setSaveMode] = useState('create_and_save');
  const [extractionStatus, setExtractionStatus] = useState(null);
  const [extractionMessage, setExtractionMessage] = useState('');
  const [extractionDetail, setExtractionDetail] = useState('');
  const [textPreviewOpen, setTextPreviewOpen] = useState(true);
  const [extractionWarnings, setExtractionWarnings] = useState([]);

  const resetDrawerState = () => {
    cleanupFundingPreviewUrls(documents);
    setDocuments([]);
    setDefaultTypes(DEFAULT_UPLOAD_TYPES);
    setWorkflowStep('upload');
    setScanning(false);
    setSaving(false);
    setConverting(false);
    setReviewFields([]);
    setClientData(null);
    setSaveMode('create_and_save');
    setExtractionStatus(null);
    setExtractionMessage('');
    setExtractionDetail('');
    setTextPreviewOpen(true);
    setExtractionWarnings([]);
  };

  React.useEffect(() => {
    if (!open) {
      resetDrawerState();
      return;
    }
    if (initialDocuments.length) {
      setDocuments(initialDocuments);
      setWorkflowStep('upload');
    }
    // Intentionally omit documents from deps — cleanup only on close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDocuments]);

  const hasExtractedFields = extractionStatus === EXTRACTION_STATUS.EXTRACTED
    || extractionStatus === EXTRACTION_STATUS.PARTIAL;
  const extractionPartial = extractionStatus === EXTRACTION_STATUS.PARTIAL;

  const extractionUnavailable = isExtractionUnavailable(extractionStatus, documents);
  const isExistingClientFlow = Boolean(existingClientId);
  const isNewClientFlow = !isExistingClientFlow;

  const hasMinimumFields = useMemo(
    () => hasMinimumExtractedClientData(reviewFields, clientData),
    [reviewFields, clientData],
  );

  const saveDocumentsToProfile = useMemo(
    () => documents.some((doc) => doc.saveToClientProfile !== false),
    [documents],
  );

  const textPreviewEntries = useMemo(
    () => documents
      .filter((doc) => String(doc.textPreview || doc.rawText || '').trim())
      .map((doc) => ({
        fileName: doc.fileName,
        textPreview: doc.textPreview || String(doc.rawText || '').slice(0, 500),
        warnings: doc.extractionWarnings || [],
      })),
    [documents],
  );

  const showTextOnlyPartial = extractionPartial && !reviewFields.length && (
    textPreviewEntries.length > 0 || extractionWarnings.length > 0
  );

  const groupedReviewFields = useMemo(
    () => groupReviewFieldsByCategory(reviewFields.filter((row) => row.extracted)),
    [reviewFields],
  );

  const documentTypeOverrideHints = useMemo(
    () => extractionWarnings.filter((warning) => warning.startsWith('FundGenius detected this document as')),
    [extractionWarnings],
  );

  const hasIgnoredOcrWarning = extractionWarnings.includes(EXTRACTION_IGNORED_OCR_WARNING);

  const saveModeOptions = useMemo(() => ({
    existingClientId,
    hasExtractedFields,
    hasMinimumFields,
    extractionUnavailable,
    extractionPartial,
    saveDocumentsToProfile,
  }), [
    existingClientId,
    hasExtractedFields,
    hasMinimumFields,
    extractionUnavailable,
    extractionPartial,
    saveDocumentsToProfile,
  ]);

  const availableSaveModes = useMemo(
    () => getAvailableSaveModes(saveModeOptions),
    [saveModeOptions],
  );

  const showExtractionDecisionList = hasExtractedFields && !extractionUnavailable;

  React.useEffect(() => {
    if (!open || extractionUnavailable) return;
    if (!isSaveModeAvailable(saveMode, saveModeOptions)) {
      setSaveMode(resolveDefaultSaveMode(saveModeOptions));
    }
  }, [open, saveMode, saveModeOptions, extractionUnavailable]);

  const activeStepIndex = useMemo(() => {
    if (workflowStep === 'review' || workflowStep === 'create') return 2;
    if (workflowStep === 'scan' || scanning) return 1;
    return 0;
  }, [workflowStep, scanning]);

  const canSaveDocumentsOnly = Boolean(existingClientId);

  const handleFiles = async (fileList) => {
    const rawFiles = Array.from(fileList || []);
    if (!rawFiles.length) return;

    setConverting(true);
    const { accepted, rejected } = await normalizeFundingUploadFiles(rawFiles);
    setConverting(false);

    rejected.forEach((item) => {
      toast.error(`${item.fileName}: ${item.message}`);
    });

    if (!accepted.length) return;

    const next = accepted.map((normalized) => createUploadedDocument(normalized.file, {
      defaultTypes,
      normalized,
    }));

    setDocuments((prev) => [...prev, ...next]);
    setReviewFields([]);
    setClientData(null);
    setExtractionStatus(null);
    setExtractionMessage('');
    setExtractionDetail('');
    setWorkflowStep('upload');

    if (accepted.some((item) => item.convertedFromHeic)) {
      toast.success('Converted from HEIC');
    }
  };

  const updateDocument = (nextDoc) => {
    setDocuments((prev) => prev.map((doc) => (doc.id === nextDoc.id ? nextDoc : doc)));
  };

  const removeDocument = (docId) => {
    setDocuments((prev) => {
      const target = prev.find((doc) => doc.id === docId);
      cleanupFundingPreviewUrls(target ? [target] : []);
      return prev.filter((doc) => doc.id !== docId);
    });
    setReviewFields([]);
    setClientData(null);
    setExtractionStatus(null);
    setExtractionMessage('');
    setExtractionDetail('');
    setWorkflowStep('upload');
  };

  const handleScan = async () => {
    const validationError = validateUploadedDocuments(documents);
    if (!documents.length) {
      toast.error('Upload at least one document');
      return;
    }
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setWorkflowStep('scan');
    setScanning(true);
    setExtractionStatus(null);
    setExtractionMessage('');
    setExtractionDetail('');
    setDocuments((prev) => prev.map((doc) => (
      doc.useForExtraction ? { ...doc, scanStatus: EXTRACTION_STATUS.SCANNING } : doc
    )));

    const result = await extractFromUploadedDocuments(documents);

    setDocuments((prev) => prev.map((doc) => {
      const updated = result.documents?.find((item) => item.id === doc.id)
        || result.documents?.find((item) => item.fileName === doc.fileName);
      if (!updated) return doc;
      return {
        ...doc,
        scanStatus: updated.scanStatus,
        extractionConfidence: updated.extractionConfidence,
        extractedFields: updated.extractedFields,
        textPreview: updated.textPreview,
        rawText: updated.rawText,
        extractionWarnings: updated.warnings,
        documentTypeUsed: updated.documentTypeUsed,
        selectedDocumentType: updated.selectedDocumentType,
      };
    }));

    setReviewFields(result.fields || []);
    setClientData(result.clientData);
    setExtractionStatus(result.status);
    setExtractionMessage(result.message || '');
    setExtractionDetail(result.detail || '');
    setExtractionWarnings(result.warnings || []);
    setTextPreviewOpen(true);
    setScanning(false);
    setWorkflowStep('review');

    const nextOptions = {
      existingClientId,
      hasExtractedFields: (
        result.status === EXTRACTION_STATUS.EXTRACTED
        || result.status === EXTRACTION_STATUS.PARTIAL
      ),
      hasMinimumFields: hasMinimumExtractedClientData(result.fields || [], result.clientData),
      extractionUnavailable: result.status === EXTRACTION_STATUS.UNAVAILABLE,
      extractionPartial: result.status === EXTRACTION_STATUS.PARTIAL,
      saveDocumentsToProfile: documents.some((doc) => doc.saveToClientProfile !== false),
    };

    if (result.status === EXTRACTION_STATUS.EXTRACTED && result.fields?.length) {
      setSaveMode(resolveDefaultSaveMode(nextOptions));
      toast.success(EXTRACTION_COMPLETE_TITLE);
    } else if (result.status === EXTRACTION_STATUS.PARTIAL) {
      setSaveMode(resolveDefaultSaveMode(nextOptions));
      toast.warning(result.detail || EXTRACTION_PARTIAL_TITLE);
    } else if (result.status === EXTRACTION_STATUS.ERROR) {
      setSaveMode(resolveDefaultSaveMode(nextOptions));
      toast.error(result.message || 'Document extraction failed');
    } else {
      setSaveMode(resolveDefaultSaveMode(nextOptions));
      toast.info(result.message || extractionMessage || EXTRACTION_UNAVAILABLE_TITLE);
    }
  };

  const handleBackToOptions = () => {
    onBackToOptions?.(documents);
  };

  const handleContinueManualIntake = () => {
    onContinueManualIntake?.({ documents });
  };

  const handleDiscard = () => {
    cleanupFundingPreviewUrls(documents);
    setDocuments([]);
    setReviewFields([]);
    setClientData(null);
    setWorkflowStep('upload');
    setSaveMode('create_and_save');
    setExtractionStatus(null);
    setExtractionMessage('');
    setExtractionDetail('');
    toast.info('Uploads and extraction cleared');
  };

  const handleSaveDocumentsOnly = async () => {
    if (!canSaveDocumentsOnly) {
      toast.error(SAVE_DOCS_ONLY_REQUIRES_CLIENT_MESSAGE);
      return;
    }
    if (!agencyId) {
      toast.error('Select an agency before saving documents.');
      return;
    }
    if (!documents.length) {
      toast.error('Upload at least one document');
      return;
    }

    setSaving(true);
    const result = await saveUploadedDocumentsToClientSafe(existingClientId, agencyId, documents);
    setSaving(false);

    if (result.authExpired) {
      toast.error(result.error);
      onAuthError?.();
      return;
    }
    if (result.error) {
      toast.error(result.error);
      return;
    }

    toast.success(`Saved ${result.saved.length} document(s) to client vault`);
    onOpenChange(false);
  };

  const handleFinalize = async () => {
    if (saveMode === 'discard') {
      handleDiscard();
      return;
    }

    if (saveMode === 'save_docs_only') {
      await handleSaveDocumentsOnly();
      return;
    }

    if (!hasExtractedFields) {
      toast.error('No extracted fields available. Continue with manual intake to create a client.');
      return;
    }

    if (!hasMinimumFields) {
      if (extractionPartial && (saveMode === 'create_and_save' || saveMode === 'create_info_only')) {
        onContinueManualIntake?.({ documents, saveMode, extractionPartial: true });
        onOpenChange(false);
        return;
      }
      toast.error('Extracted data is missing required client fields. Continue with manual intake instead.');
      return;
    }

    if (!clientData && !reviewFields.length) {
      toast.error('Scan documents first');
      return;
    }

    const merged = clientDataFromReviewFields(reviewFields, clientData || {});
    const payload = {
      clientData: merged,
      documents: documents.filter((doc) => doc.saveToClientProfile || saveMode === 'create_and_save'),
      saveMode,
      reviewFields: reviewFields.filter((row) => row.apply),
      saveDocuments: saveMode === 'create_and_save',
      infoOnly: saveMode === 'create_info_only',
    };

    if (saveMode === 'create_info_only') {
      toast.warning('Uploaded files will not be saved to the client profile.');
    }

    onCreateClient?.(payload);
    onOpenChange(false);
  };

  const finalizeDisabled = saving || (
    saveMode === 'save_docs_only'
      ? !canSaveDocumentsOnly || !documents.length
      : (saveMode === 'create_and_save' || saveMode === 'create_info_only')
        ? (
          !hasExtractedFields
          || (!hasMinimumFields && !(extractionPartial && saveMode === 'create_and_save' && saveDocumentsToProfile))
        )
        : false
  );

  const finalizeLabel = (() => {
    if (saveMode === 'save_docs_only') return saving ? 'Saving documents…' : 'Save documents';
    if (saveMode === 'discard') return 'Discard extraction';
    if (extractionPartial && !hasMinimumFields && saveMode === 'create_and_save') {
      return 'Continue with manual intake';
    }
    if (hasExtractedFields) return 'Create client from extraction';
    return 'Continue with manual intake';
  })();

  const scanDisabled = scanning || converting || !documents.length
    || documents.some((doc) => doc.conversionStatus === 'converting');

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        className={`${FUNDING_RIGHT_DRAWER_CLASS} fg-upload-drawer w-full sm:max-w-3xl`}
        data-testid="upload-documents-drawer"
      >
        <SheetHeader>
          <button
            type="button"
            className="fg-upload-back-btn"
            onClick={handleBackToOptions}
            data-testid="upload-back-to-options"
          >
            <ArrowLeft className="size-4" />
            Back to options
          </button>
          <SheetTitle>Upload Documents</SheetTitle>
          <SheetDescription>
            Upload client documents to pre-fill intake fields. Classify each file, scan for data, then review before creating the client.
          </SheetDescription>
        </SheetHeader>

        <div className="fg-upload-drawer__body">
          <div className="fg-upload-workflow" aria-label="Upload workflow">
            {STEPS.map((step, index) => (
              <span
                key={step.id}
                className={`fg-upload-workflow__step ${index === activeStepIndex ? 'is-active' : ''} ${index < activeStepIndex ? 'is-done' : ''}`}
              >
                {index < activeStepIndex ? '✓ ' : ''}
                {step.label}
              </span>
            ))}
          </div>

          {workflowStep === 'upload' || workflowStep === 'scan' ? (
            <>
              <div className="fg-upload-panel">
                <div
                  className="fg-upload-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleFiles(e.dataTransfer.files);
                  }}
                >
                  <FileUp className="mx-auto size-10 text-[var(--fg-accent)]" />
                  <strong>{FUNDING_UPLOAD_DROPZONE_LABEL}</strong>
                  <span className="fg-upload-dropzone__hint">
                    {converting
                      ? 'Converting iPhone photo…'
                      : 'Multiple files supported · Drag and drop or choose from your device'}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={FUNDING_UPLOAD_ACCEPT}
                    className="hidden"
                    data-testid="upload-documents-input"
                    onChange={(e) => {
                      handleFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="fg-btn fg-btn-light mt-4"
                    disabled={converting}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {converting ? 'Converting…' : 'Choose files'}
                  </button>
                </div>
              </div>

              <div className="fg-upload-default-type">
                <FundingDocumentTypeSelector
                  id="upload-default-doc-types"
                  label="Default types for new uploads"
                  value={defaultTypes}
                  onChange={setDefaultTypes}
                />
                <p className="fg-upload-default-type__hint">New files inherit these tags. You can change types on each document card.</p>
              </div>

              {documents.length > 0 ? (
                <div className="fg-upload-doc-list" data-testid="upload-file-list">
                  {documents.map((document) => (
                    <FundingUploadedDocumentCard
                      key={document.id}
                      document={document}
                      onChange={updateDocument}
                      onRemove={removeDocument}
                    />
                  ))}
                </div>
              ) : null}
            </>
          ) : null}

          {workflowStep === 'review' || workflowStep === 'create' ? (
            <>
              {hasExtractedFields && !extractionUnavailable ? (
                <div className="fg-extract-preview" data-testid="extracted-fields-preview">
                  <h4>Review extracted fields</h4>
                  {extractionPartial ? (
                    <p
                      className="text-sm text-amber-700 dark:text-amber-200"
                      data-testid="extraction-partial-warning"
                    >
                      {showTextOnlyPartial
                        ? EXTRACTION_PARTIAL_MANUAL_HINT
                        : (extractionMessage || extractionDetail || EXTRACTION_PARTIAL_TITLE)}
                    </p>
                  ) : null}
                  {showTextOnlyPartial ? (
                    <details
                      className="fg-extract-text-preview"
                      data-testid="extracted-text-preview"
                      open={textPreviewOpen}
                      onToggle={(event) => setTextPreviewOpen(event.currentTarget.open)}
                    >
                      <summary>Extracted text preview</summary>
                      {textPreviewEntries.map((entry) => (
                        <div key={entry.fileName} className="fg-extract-text-preview__doc">
                          <strong>{entry.fileName}</strong>
                          <pre>{entry.textPreview}</pre>
                          {entry.warnings?.length ? (
                            <ul className="fg-extract-text-preview__warnings" data-testid="extraction-field-warnings">
                              {entry.warnings.map((warning) => (
                                <li key={warning}>{warning}</li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ))}
                    </details>
                  ) : null}
                  {extractionWarnings.length && showTextOnlyPartial ? (
                    <ul className="fg-extract-text-preview__warnings" data-testid="extraction-aggregate-warnings">
                      {extractionWarnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  ) : null}
                  {documentTypeOverrideHints.map((hint) => (
                    <p
                      key={hint}
                      className="text-sm text-[var(--funding-text-secondary)]"
                      data-testid="document-type-override-hint"
                    >
                      {hint}
                    </p>
                  ))}
                  {hasIgnoredOcrWarning ? (
                    <p
                      className="text-sm text-amber-700 dark:text-amber-200"
                      data-testid="ignored-ocr-warning"
                    >
                      {EXTRACTION_IGNORED_OCR_WARNING}
                    </p>
                  ) : null}
                  {reviewFields.length ? (
                    <>
                      <p className="text-sm text-[var(--funding-text-secondary)]">
                        Confirm which extracted values to apply. Source document and confidence are shown for each match.
                      </p>
                      <div className="fg-extract-review-table-wrap" data-testid="upload-review-table">
                        {groupedReviewFields.map(({ category, fields }) => (
                          <div key={category} className="fg-extract-review-group" data-testid={`review-group-${category.toLowerCase()}`}>
                            <h5 className="fg-extract-review-group__title">{category}</h5>
                            <table className="fg-extract-review-table">
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Current</th>
                                  <th>Extracted</th>
                                  <th>Source</th>
                                  <th>Confidence</th>
                                  <th>Apply</th>
                                </tr>
                              </thead>
                              <tbody>
                                {fields.map((row) => (
                                  <tr key={row.id} data-testid={`review-field-${row.fieldKey || row.id}`}>
                                    <td>{row.field}</td>
                                    <td>{row.current}</td>
                                    <td>{row.extracted}</td>
                                    <td>{row.source}</td>
                                    <td>{row.confidence != null ? `${row.confidence}%` : '—'}</td>
                                    <td>
                                      <input
                                        type="checkbox"
                                        checked={Boolean(row.apply)}
                                        aria-label={`Apply ${row.field}`}
                                        onChange={(e) => {
                                          setReviewFields((prev) => prev.map((item) => (
                                            item.id === row.id ? { ...item, apply: e.target.checked } : item
                                          )));
                                        }}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-[var(--funding-text-secondary)]">
                      Structured fields were not parsed automatically. Continue with manual intake or upload another document.
                    </p>
                  )}
                </div>
              ) : null}

              {extractionUnavailable ? (
                <div
                  className="fg-extraction-unavailable"
                  data-testid="extraction-unavailable-panel"
                >
                  <h4>{EXTRACTION_UNAVAILABLE_TITLE}</h4>
                  <p>{extractionMessage || extractionDetail || EXTRACTION_UNAVAILABLE_DETAIL}</p>

                  {isNewClientFlow ? (
                    <div
                      className="fg-manual-intake-fallback"
                      data-testid="manual-intake-fallback-card"
                    >
                      <h4>Continue with manual intake</h4>
                      <p>
                        {extractionMessage || extractionDetail || 'Text extraction could not read this file. Your uploaded documents will stay attached as references while you enter the client details manually.'}
                      </p>
                      <p className="fg-manual-intake-fallback__note">
                        Documents will be available as reference notes in the manual intake step.
                      </p>
                      <div className="fg-extraction-unavailable__actions">
                        <button
                          type="button"
                          className="funding-action-button funding-action-button--primary"
                          onClick={handleContinueManualIntake}
                          data-testid="continue-manual-intake-btn"
                        >
                          Continue with manual intake
                        </button>
                        <button
                          type="button"
                          className="funding-action-button funding-action-button--muted"
                          onClick={() => setWorkflowStep('upload')}
                          data-testid="back-to-upload-btn"
                        >
                          Back to upload
                        </button>
                      </div>
                      <button
                        type="button"
                        className="fg-text-btn"
                        onClick={handleDiscard}
                        data-testid="clear-uploaded-documents-btn"
                      >
                        Clear uploaded documents
                      </button>
                    </div>
                  ) : (
                    <div className="fg-extraction-unavailable__actions">
                      <button
                        type="button"
                        className="funding-action-button funding-action-button--secondary"
                        data-testid="save-documents-only-inline-btn"
                        disabled={saving || !documents.length}
                        onClick={handleSaveDocumentsOnly}
                      >
                        {saving ? 'Saving documents…' : 'Save documents only'}
                      </button>
                      <button
                        type="button"
                        className="funding-action-button funding-action-button--secondary"
                        onClick={handleContinueManualIntake}
                        data-testid="continue-manual-intake-btn"
                      >
                        Add notes manually
                      </button>
                      <button
                        type="button"
                        className="funding-action-button funding-action-button--muted"
                        onClick={() => setWorkflowStep('upload')}
                        data-testid="back-to-upload-btn"
                      >
                        Back to upload
                      </button>
                    </div>
                  )}
                </div>
              ) : null}

              {showExtractionDecisionList ? (
                <div className="fg-upload-save-modes" data-testid="upload-save-modes">
                  <h4>What should FundGenius do with these documents?</h4>
                  <div className="fg-upload-save-modes__grid">
                    {SAVE_MODES.map((mode) => {
                      const enabled = availableSaveModes.some((item) => item.id === mode.id);
                      return (
                        <label
                          key={mode.id}
                          className={`fg-upload-save-mode ${saveMode === mode.id ? 'is-selected' : ''} ${enabled ? '' : 'is-disabled'}`}
                          data-testid={`upload-save-mode-${mode.id}`}
                        >
                          <input
                            type="radio"
                            name="upload-save-mode"
                            value={mode.id}
                            checked={saveMode === mode.id}
                            disabled={!enabled}
                            onChange={() => {
                              setSaveMode(mode.id);
                              setWorkflowStep('create');
                            }}
                          />
                          <strong>{mode.title}</strong>
                          <span>{mode.description}</span>
                          {mode.id === 'save_docs_only' && !enabled ? (
                            <span className="fg-upload-save-mode-hint">{SAVE_DOCS_ONLY_REQUIRES_CLIENT_MESSAGE}</span>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="fg-upload-doc-list">
                {documents.map((document) => (
                  <FundingUploadedDocumentCard
                    key={document.id}
                    document={document}
                    onChange={updateDocument}
                    onRemove={removeDocument}
                  />
                ))}
              </div>
            </>
          ) : null}
        </div>

        <div className="funding-upload-drawer__footer funding-drawer-footer">
          {workflowStep === 'upload' || workflowStep === 'scan' ? (
            <>
              <button
                type="button"
                className="funding-action-button funding-action-button--secondary"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="funding-action-button funding-action-button--primary"
                data-testid="scan-documents-btn"
                disabled={scanDisabled}
                onClick={handleScan}
              >
                <ScanLine className="funding-action-button__icon" aria-hidden />
                {scanning ? 'Scanning documents…' : converting ? 'Converting…' : 'Scan / extract fields'}
              </button>
            </>
          ) : null}
          {workflowStep === 'review' || workflowStep === 'create' ? (
            <>
              <button
                type="button"
                className="funding-action-button funding-action-button--muted"
                onClick={() => setWorkflowStep('upload')}
                data-testid="footer-back-to-upload-btn"
              >
                Back to upload
              </button>
              {hasExtractedFields && !extractionUnavailable ? (
                <>
                  {extractionPartial ? (
                    <button
                      type="button"
                      className="funding-action-button funding-action-button--secondary"
                      data-testid="continue-manual-intake-footer-btn"
                      onClick={handleContinueManualIntake}
                    >
                      {isExistingClientFlow ? 'Add notes manually' : 'Continue with manual intake'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="funding-action-button funding-action-button--primary"
                    data-testid="create-from-documents-btn"
                    disabled={finalizeDisabled}
                    onClick={handleFinalize}
                  >
                    {finalizeLabel}
                  </button>
                </>
              ) : (
                <>
                  {canSaveDocumentsOnly ? (
                    <button
                      type="button"
                      className="funding-action-button funding-action-button--secondary"
                      data-testid="save-documents-only-btn"
                      disabled={saving || !documents.length}
                      onClick={handleSaveDocumentsOnly}
                    >
                      {saving ? 'Saving documents…' : 'Save documents only'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="funding-action-button funding-action-button--primary"
                    data-testid="continue-manual-intake-footer-btn"
                    onClick={handleContinueManualIntake}
                  >
                    {isExistingClientFlow ? 'Add notes manually' : 'Continue with manual intake'}
                  </button>
                </>
              )}
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UploadDocumentsDrawer;
